import { FileText } from "lucide-react";
import { ContextAttachmentChips } from "./ContextAttachmentChips.js";
import { AssistantMessageActions } from "./AssistantMessageActions.js";
import { MessageReferencePreview } from "./MessageReferencePreview.js";
import { StructuredUserText } from "./StructuredUserText.js";
import { MarkdownContent } from "../widgets/markdownText/MarkdownContent.js";
import { InlineWidget } from "./PlayerAIInlineWidgets";
import { SystemNoticeBubble } from "./PlayerAISystemNotice.js";
import type {
  PlayerAIChatMessage,
  PlayerAIMessageFeedback,
  PlayerAIMessageFeedbackRequest,
  PlayerAIMessageReference,
  PlayerAIWidgetActionHandler,
} from "./playerai.types";

/**
 * One message in the Player AI thread (PB-5422 chat-parts-private): the
 * user, group-assistant, assistant and system presentations. The system
 * branch lives in PlayerAISystemNotice.tsx, the user text in
 * StructuredUserText.tsx and the response actions in
 * AssistantMessageActions.tsx, to keep this file under the 200-line soft cap
 * (packages/ui/CLAUDE.md invariant 6).
 */
export function MessageBubble({
  m,
  onWidgetAction,
  onMessageFeedback,
  groupConversation,
  onReply,
  onOpenReference,
}: {
  m: PlayerAIChatMessage;
  onWidgetAction?: PlayerAIWidgetActionHandler | undefined;
  onMessageFeedback?:
    | ((
        feedback: PlayerAIMessageFeedbackRequest,
      ) => Promise<PlayerAIMessageFeedback>)
    | undefined;
  groupConversation: boolean;
  onReply?: ((message: PlayerAIChatMessage) => void) | undefined;
  onOpenReference?: ((reference: PlayerAIMessageReference) => void) | undefined;
}) {
  if (m.role === "system") {
    return <SystemNoticeBubble m={m} onWidgetAction={onWidgetAction} />;
  }
  if (m.role === "user") {
    return (
      <div className="flex flex-col items-end">
        <div
          data-message-bubble
          className="max-w-[85%] min-w-0 space-y-2 bg-pb-card border border-pb-border rounded-lg rounded-ee-sm px-3 py-2 text-sm text-pb-text leading-snug whitespace-pre-wrap break-words transition-shadow"
        >
          {m.replyTo ? (
            <MessageReferencePreview
              reference={m.replyTo}
              onOpen={onOpenReference}
            />
          ) : null}
          {m.contextAttachments?.length ? (
            <ContextAttachmentChips items={m.contextAttachments} />
          ) : null}
          {m.content ? (
            <div>
              <StructuredUserText
                text={m.content}
                highlightedOccurrences={
                  m.playerAIMentionOccurrenceIndices ?? []
                }
                personMentions={m.personMentions ?? []}
              />
            </div>
          ) : null}
          {m.attachments?.length ? (
            <ul className="flex flex-wrap gap-1.5" aria-label="Attached files">
              {m.attachments.map((attachment) => (
                <li
                  key={attachment.id}
                  className="inline-flex min-w-0 items-center gap-1 rounded-md border border-pb-border bg-pb-shell/70 px-2 py-1 text-dense-2 text-pb-text-secondary"
                >
                  <FileText className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="max-w-48 truncate">{attachment.name}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {groupConversation ? (
          <span className="mt-1 px-1 text-dense-2 text-pb-text-muted">
            {m.senderName ?? "Participant"}
          </span>
        ) : null}
      </div>
    );
  }
  const response = m.content ? (
    <div
      data-message-bubble
      className="w-full min-w-0 bg-pb-iris-soft/30 border border-pb-iris/20 rounded-lg rounded-es-sm px-3 py-2 text-sm text-pb-text leading-snug transition-shadow"
    >
      {groupConversation && m.triggerPreview ? (
        <MessageReferencePreview
          reference={m.triggerPreview}
          additionalCount={m.triggerPreview.additionalCount}
          onOpen={onOpenReference}
        />
      ) : null}
      <MarkdownContent
        markdown={m.content}
        tableLayout="scroll"
        className="break-words leading-snug [&_p]:first:mt-0 [&_p]:last:mb-0"
      />
      {m.stopped ? (
        <p className="mt-1 text-dense-2 font-medium text-pb-text-muted">
          Stopped
        </p>
      ) : null}
    </div>
  ) : null;
  const actions = (
    <AssistantMessageActions
      message={m}
      onReply={onReply}
      onMessageFeedback={onMessageFeedback}
    />
  );
  const widget = m.widget ? (
    <div className="w-full max-w-md min-w-0">
      <InlineWidget
        payload={m.widget}
        {...(onWidgetAction !== undefined ? { onAction: onWidgetAction } : {})}
      />
    </div>
  ) : null;
  if (groupConversation) {
    return (
      <div className="flex justify-start">
        <div className="min-w-0 flex-1 flex flex-col items-start gap-1.5">
          {response ? (
            <div className="group/assistant max-w-[90%] min-w-0 flex flex-col items-start">
              {response}
              <span className="mt-0.5 px-1 text-dense-2 text-pb-text-muted">
                Player AI
              </span>
              {actions}
            </div>
          ) : null}
          {widget}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="min-w-0 flex-1 flex flex-col items-start gap-1.5">
        {response ? (
          <div className="group/assistant max-w-[85%] min-w-0 flex flex-col items-end">
            {response}
            {actions}
          </div>
        ) : null}
        {widget}
      </div>
    </div>
  );
}
